import { useSuspenseQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { useEffect, useMemo } from 'react';
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';
import { z } from 'zod';
import { en, zhTW } from 'zod/locales';
import { SharedConfig } from '@_/config/shared-config.ts';
import { createZodErrorMap } from '@_/form-validators';
import { LocaleCodes } from '@_/static';
import { getClosestLocale, getLanguageFromLocale } from '@_/utils';
import { useStores } from '#src/state/StoreContext.tsx';
import { loadI18nMessages } from '#src/utils/i18nMessageUtil.ts';

const intlCache = createIntlCache();

export function I18nProvider({
  children,
  ...props
}: React.PropsWithChildren<{
  locale?: string;
  i18nMessages?: Record<string, string>;
}>) {
  const { useAuthStore } = useStores();

  const locale =
    props.locale ||
    getClosestLocale(navigator?.language, LocaleCodes) ||
    SharedConfig.defaultLocale;

  const messagesQuery = useSuspenseQuery({
    queryKey: ['i18nMessages', locale],
    queryFn: async () =>
      props.i18nMessages ? props.i18nMessages : loadI18nMessages(locale, SharedConfig.defaultLocale)
  });

  const messages = messagesQuery.data;

  const intl = useMemo(() => {
    return createIntl({ locale, messages }, intlCache);
  }, [messages, locale]);

  useEffect(() => {
    useAuthStore.getState().setLocale(locale);
  }, [locale, useAuthStore]);

  useEffect(() => {
    // Only save cookie if locale was set explicitly
    if (props.locale) {
      Cookies.set(SharedConfig.cookies.locale, props.locale, {
        domain: import.meta.env.ENV_SHARED_COOKIE_DOMAIN_NAME,
        expires: 356,
        sameSite: 'lax'
      });
    }
  }, [props.locale]);

  useEffect(() => {
    if (getLanguageFromLocale(locale) === 'en') {
      z.config({
        ...en(),
        customError: intl ? createZodErrorMap({ intl }) : undefined
      });
    } else {
      z.config({
        ...zhTW(),
        customError: intl ? createZodErrorMap({ intl }) : undefined
      });
    }
  }, [intl, locale]);

  return (
    <RawIntlProvider key={locale} value={intl}>
      {children}
    </RawIntlProvider>
  );
}
